/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2020, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'react',
	'prop-types',
	'@ba/ba-ui-carbon-toolkit',
	'./ColorPicker',
	'./FontPropTypes',
	'../../../../nls/StringResources',
	'@ba/ba-graphics/dist/icons-js/reset_32',
	'@ba/ba-graphics/dist/icons-js/text--bold_32',
	'@ba/ba-graphics/dist/icons-js/text--italic_32',
	'@ba/ba-graphics/dist/icons-js/text--underline_32',
	'@ba/ba-graphics/dist/icons-js/center-align_32',
	'@ba/ba-graphics/dist/icons-js/text--align--left_32',
	'@ba/ba-graphics/dist/icons-js/text--align--right_32',
	'@ba/ba-graphics/dist/icons-js/text-wrap_32',
	'@ba/ba-graphics/dist/icons-js/information_32'

], function (
	_,
	React,
	PropTypes,
	Toolkit,
	ColorPicker,
	FontPropTypes,
	StringResources,
	reset32,
	bold32,
	italic32,
	underline32,
	centerAlign32,
	leftAlign32,
	rightAlign32,
	textWrap32,
	information32) {

	const resetIcon = reset32.default;
	const boldIcon = bold32.default;
	const italicIcon = italic32.default;
	const underlineIcon = underline32.default;
	const leftAlignIcon = leftAlign32.default;
	const centerAlignIcon = centerAlign32.default;
	const rightAlignIcon = rightAlign32.default;
	const textWrapIcon = textWrap32.default;
	const infoIcon = information32.default;

	const {
		Button,
		FlexItem,
		FlexLayout,
		IconButtonToggleGroup,
		Label,
		Select,
		TextInput,
		ToggleSwitch,
		Tooltip,
		SVGIcon,
		Checkbox
	} = Toolkit;

	const {
		TextPropType,
		FontOptionsPropType,
		ColorOptionsPropType,
		DefaultStatePropType,
		ALIGN_ENUM
	} = FontPropTypes;

	const DEFAULT_VALUES = {
		color: '#000000',
		textAlign: ALIGN_ENUM.LEFT,
		bold: false,
		italic: false,
		underline: false,
		textWrap: false,
		title: '',
		labelShadow:false,
		contrastLabelColor: false,
		applyToAll: false
	};

	function getStateKeys({ supportsStyleAndAlignment, supportsTextWrap, supportsContrastLabelColor, supportsLabelShadow, supportsApplyToAll,supportsTitle, supportsFontStyle }) {
		const keys = ['color', 'font', 'fontSize'];

		if(supportsStyleAndAlignment) {
			keys.push('textAlign');
			keys.push('bold');
			keys.push('italic');
			keys.push('underline');

			if(supportsTextWrap) {
				keys.push('textWrap');
			}
		}

		if (supportsContrastLabelColor){
			keys.push('contrastLabelColor');
		}

		if (supportsLabelShadow){
			keys.push('labelShadow');
		}

		if (supportsTitle){
			keys.push('title');
		}

		if (supportsApplyToAll){
			keys.push('applyToAll');
			keys.push('applyToAllInitState');
		}

		if (supportsFontStyle){
			keys.push('fontStyle');
		}


		return keys;
	}

	const baseClassName = 'dashboardTextFormatView';
	const baseTid = 'textFormatView';

	class TextFormatView extends React.Component {
		static propTypes = {
			state: TextPropType,
			onStateChange: PropTypes.func.isRequired,
			onClose: PropTypes.func.isRequired,
			supportsStyleAndAlignment: PropTypes.bool,
			supportsTextWrap: PropTypes.bool,
			supportsLabelShadow: PropTypes.bool,
			supportsApplyToAll: PropTypes.bool,
			supportsFontStyle: PropTypes.bool,
			supportsContrastLabelColor: PropTypes.bool,
			supportsTitle:PropTypes.bool,
			colorOptions: ColorOptionsPropType.isRequired,
			fontOptions: FontOptionsPropType.isRequired,
			fontSizeOptions: FontOptionsPropType.isRequired,
			groupedProperties: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
			defaultState: DefaultStatePropType.isRequired,
			disableFontSizeAndColor: PropTypes.bool
		};

		static defaultProps = {
			defaultState: {},
			supportsStyleAndAlignment: false,
			supportsTextWrap: false,
			supportsContrastLabelColor: false,
			supportsLabelShadow: false,
			supportsApplyToAll: false,
			supportsFontStyle: false,
			disableFontSizeAndColor: false
		}

		constructor(props) {
			super(props);

			this.initState = this._buildInitState();
			this.defaultState = this._buildDefaultState();
		}

		_buildInitState() {
			// Clone state
			const initSate = {
				...this.props.state
			};
			if (this.props.state.applyToAllInitState) {
				delete this.props.state.applyToAllInitState;
			}
			return initSate;
		}

		_buildDefaultState() {
			const defaultState = {};

			const defaultStateKeys = _.union(
				Object.keys(this.props.defaultState),
				Object.keys(DEFAULT_VALUES)
			);

			// It's possible for default state keys to be set even if they're not exposed to the user,
			// so iterate over all of them, not just the ones returned by getStateKeys()
			defaultStateKeys.forEach(
				key => defaultState[key] = this._getStateValue(this.props.defaultState[key], DEFAULT_VALUES[key], null)
			);
			return defaultState;
		}

		// Getters

		getState() {
			const stateProp = this._sanitizeInputState(this.props.state);

			const state = {};
			this.getStateKeys().forEach(
				key => {
					const propValue = stateProp[key];
					const defaultValue = this.defaultState[key];
					state[key] = this._getStateValue(propValue, defaultValue);
				}
			);

			return state;
		}

		getStateKeys() {
			return getStateKeys({
				supportsStyleAndAlignment: this.props.supportsStyleAndAlignment,
				supportsTextWrap: this.props.supportsTextWrap,
				supportsContrastLabelColor: this.props.supportsContrastLabelColor,
				supportsLabelShadow: this.props.supportsLabelShadow,
				supportsApplyToAll: this.props.supportsApplyToAll,
				supportsTitle: this.props.supportsTitle,
				supportsFontStyle: this.props.supportsFontStyle
			});
		}

		_validateFontValue(fontValue, fontStyle, legalValues){
			let found = legalValues.find((legalValue) => {
				return legalValue.value === fontValue || legalValue.subCssClasses.map(({ value }) => value).indexOf(fontValue) > -1;
			});
			if (found){
				if (found.value === fontValue){
					return {
						font: fontValue,
						fontStyle: fontStyle
					};
				}else{
					return {
						font: found.value,
						fontStyle: fontValue
					};
				}
			}
			return {
				font: undefined,
				fontStyle: undefined
			};
		}

		_sanitizeInputState(state) {
			let { font, fontStyle,fontSize, ...otherState } = state;

			let fontInfo = this._validateFontValue(font, fontStyle, this.props.fontOptions.map(({ value, subCssClasses }) => subCssClasses ? { value:value, subCssClasses:subCssClasses }: { value:value,subCssClasses:[] }));
			font = fontInfo.font;
			fontStyle = fontInfo.fontStyle;
			fontSize = this._validateValue(fontSize, this.props.fontSizeOptions.map(({ value }) => value));
			// N.B. we could also sanitize the color, however the default colour for many VIDA
			// visualizations is not one passed to TextFormatView as a colour option - so if
			// we sanitized it, we'd lose the colour.

			return {
				font,
				fontStyle,
				fontSize,
				...otherState
			};
		}

		_sanitizeOutputState(state) {

			let { title, labelShadow,contrastLabelColor,applyToAll,fontStyle, ...otherState } = state;
			let sanitizedState = {};
			const effectiveFont = this._getEffectiveFontInfo(state.font,fontStyle,this.props.fontOptions);
			fontStyle = effectiveFont.fontStyle;

			// Don't hard-code default values; rather set to undefined - we will get them for free
			// and if the default value changes (e.g. change dashboard theme), the vis will still
			// honour what the default value currently represents.
			Object.keys(otherState).forEach(
				propName => sanitizedState[propName] = this._isPropertyDefaultValue(state, propName) ? undefined : state[propName]
			);

			let outputState = {
				title,
				labelShadow,
				contrastLabelColor,
				applyToAll,
				fontStyle,
				...sanitizedState
			};
			const stateKeys = this.getStateKeys();
			Object.keys(outputState).forEach(outputKey => !stateKeys.includes(outputKey) ? delete outputState[outputKey] : {});
			return outputState;
		}


		/*
		 * Determine if a given property is its default value.
		 * Note: a property is considered default only if both it, and all other properties in its
		 * 'property group', are their default values.
		 */
		_isPropertyDefaultValue(state, propName) {
			const propertiesGroup = this._getPropertiesInGroup(propName);

			const nonDefaultProperty = propertiesGroup.find(
				propName => state[propName] !== this.defaultState[propName]
			);

			return !nonDefaultProperty;
		}

		/*
		 * The TextFormatView can be configured with 'property groups', where more than one
		 * property is grouped together. This is not reflected in the UI, but rather conceptually
		 * how properties are treated. For example, a property isn't considered to have its default
		 * value unless all the properties in its property group are their default value - because
		 * we consider them as an aggregate property in this case.
		 *
		 * This function takes a property name and return an array of all the properities in its
		 * group. If it does not belong to a group, an array of just itself is returned (a property
		 * group of one.)
		 */
		_getPropertiesInGroup(propName) {

			const propertiesGroup = this.props.groupedProperties && this.props.groupedProperties.find(
				propList => propList.indexOf(propName) >= 0
			);
			return propertiesGroup || [propName];
		}

		_validateValue(value, legalValues) {
			return legalValues.indexOf(value) > -1 ? value : null;
		}

		_isStateValueSet(value) {
			return (value !== undefined) && (value !== null);
		}

		_getStateValue(...values) {
			let value;
			while(!this._isStateValueSet(value) && values.length) {
				value = values.shift();
			}
			return value;
		}

		isDefault() {
			const state = this.getState();
			return this.getStateKeys().every(
				key => state[key] === this.defaultState[key]
			);
		}

		// Setters

		setNewState(state) {
			this.props.onStateChange(
				this._sanitizeOutputState(state)
			);
		}

		modifyState(change) {
			const state = this.getState();
			const newState = {};
			this.getStateKeys().forEach(
				key => {
					const oldValue = state[key];
					const newValue = change[key];
					newState[key] = this._getStateValue(newValue, oldValue);
				}
			);

			this.setNewState(newState);
		}

		// Event handlers

		onOkClicked() {
			this.props.onClose();
		}

		onCancelClicked() {
			this.setNewState(this.initState);
			this.props.onClose();
		}

		onRestoreButtonClicked() {
			this.setNewState(this.defaultState);
		}

		onAlignmentClick(value, alignment) {
			this.modifyState({
				textAlign: value ? alignment : ALIGN_ENUM.NONE
			});
		}

		onToggleFormatClick(key) {
			this.modifyState({
				[key]: !this.props.state[key]
			});
		}

		// Render functions

		render() {
			const state = this.getState();
			return (
				<React.Fragment>
					<FlexLayout
						className={`${baseClassName}--content`}
						direction='column'
					>
						{this.props.supportsTitle ?
							this._renderTitle(state)
							: null
						}
						{this._renderFontFamily(state)}
						{this.props.supportsFontStyle ?
							this._renderFontStyle(state)
							: null
						}
						{this.props.disableFontSizeAndColor ?
							null
							: this._renderSizeAndColour(state)
						}
						{this.props.supportsStyleAndAlignment ?
							this._renderStyleAndAligment(state)
							: null
						}
						{this.props.supportsContrastLabelColor ?
							this._renderContrastLabelColor(state)
							: null
						}
						{this.props.supportsLabelShadow ?
							this._renderLabelShadow(state)
							: null
						}
						{this.props.supportsApplyToAll ?
							this._renderApplyToAll(state)
							: null
						}
						{this._renderRestoreButton()}
					</FlexLayout>
					<FlexLayout
						className={`${baseClassName}--buttons`}
						direction='row'
					>
						{this._renderFooterButtons()}
					</FlexLayout>
				</React.Fragment>
			);
		}

		_renderTitle(state){
			const caption = StringResources.get('textItemTitle');
			return (
				<FlexLayout
					className={`${baseClassName}__row`}
					direction='column'
					data-tid={`${baseTid}_title`}
				>
					<Label
						label={caption}
						type='caption'
					/>
					<TextInput
						value={state.title}
						onValueChange={title => this.modifyState({ title })}
					/>
				</FlexLayout>
			);
		}

		_renderContrastLabelColor(state){
			const caption = StringResources.get('textContrastLabelColor');
			const toggleLabel = state.contrastLabelColor ? StringResources.get('toggleOn'): StringResources.get('toggleOff');
			return(
				<FlexLayout
					className={`${baseClassName}__row`}
					direction='column'
					data-tid={`${baseTid}_contrastLabelColor`}
				>
					<FlexItem>
						<Label
							label={caption}
							type='caption'
						/>
						<Tooltip
							title={StringResources.get('contrastLabelColorInfo')}>
							<SVGIcon
								iconId={infoIcon.id}
								verticalAlign='middle'
							/>
						</Tooltip>
					</FlexItem>
					<FlexItem>
						<ToggleSwitch
							className={`${baseClassName}__contrastLabelColor`}
							checked={state.contrastLabelColor}
							onChange={contrastLabelColor => this.modifyState({ contrastLabelColor })}
							align='right'
							label= {toggleLabel}
						/>
					</FlexItem>
				</FlexLayout>
			);
		}

		_renderLabelShadow(state){
			const caption = StringResources.get('textLabelShadow');
			const toggleLabel = state.labelShadow ? StringResources.get('toggleOn'): StringResources.get('toggleOff');
			return(
				<FlexLayout
					className={`${baseClassName}__row`}
					direction='column'
					data-tid={`${baseTid}_labelShadow`}
				>
					<FlexItem>
						<Label
							label={caption}
							type='caption'
						/>
						<Tooltip
							title={StringResources.get('labelShadowInfo')}>
							<SVGIcon
								iconId={infoIcon.id}
								verticalAlign='middle'
							/>
						</Tooltip>
					</FlexItem>
					<FlexItem>
						<ToggleSwitch
							className={`${baseClassName}__textLabelShadow`}
							checked={state.labelShadow}
							onChange={labelShadow => this.modifyState({ labelShadow })}
							align='right'
							label= {toggleLabel}
						/>
					</FlexItem>
				</FlexLayout>
			);
		}

		_renderApplyToAll(state){
			const label = StringResources.get('applyToAll');
			return(
				<FlexLayout
					className={`${baseClassName}__row`}
					direction='column'
					data-tid={`${baseTid}_applyToAll`}
				>
					<FlexItem>
						<Checkbox
							label={label}
							onChange={applyToAll => this.modifyState({ applyToAll })}
							checked={state.applyToAll}
						/>
					</FlexItem>
				</FlexLayout>
			);
		}


		_renderFontFamily(state) {
			const label = StringResources.get('fontSelect');
			return (
				<FlexLayout
					className={`${baseClassName}__row`}
					direction='column'
					data-tid={`${baseTid}_font`}
				>
					<Label
						label={label}
						type='caption'
					/>
					<Select
						options={this.props.fontOptions}
						value={state.font}
						displayLabel={label}
						onChange={font => this.modifyState({ font })}
					/>
				</FlexLayout>
			);
		}

		_findFontFamilyItemFromValue(value, items) {
			for (let i = 0; i < items.length; i++) {
				if (items[i].value === value) {
					return items[i];
				}
			}
			return null;
		}

		_getEffectiveFontInfo(font, fontStyle, fontOptions){
			let fontFamilyItem = this._findFontFamilyItemFromValue(font, fontOptions);
			if ( !fontFamilyItem || !fontFamilyItem.subCssClasses || fontFamilyItem.subCssClasses.length === 0 ){
				fontFamilyItem = fontFamilyItem || {};
				const defaultFontStyle = {
					subCssClasses: [
						{ cssClass: fontFamilyItem.cssClass,
							label: StringResources.get('regularFontStyle'),
							value: fontFamilyItem.value }]
				};
				fontStyle = fontFamilyItem.value;
				fontFamilyItem = defaultFontStyle;
			}
			if ( !fontStyle || !this._findFontFamilyItemFromValue(fontStyle, fontFamilyItem.subCssClasses)){
				let item = this._findFontFamilyItemFromValue(font, fontFamilyItem.subCssClasses);
				fontStyle = item ? item.value: fontFamilyItem.subCssClasses[0].value;
			}

			return {
				fontStyle: fontStyle,
				fontFamilyItem: fontFamilyItem
			};

		}

		_renderFontStyle(state) {
			const label = StringResources.get('fontFamilyStyle');
			const effectiveFont = this._getEffectiveFontInfo(state.font,state.fontStyle,this.props.fontOptions);
			const fontFamilyItem = effectiveFont.fontFamilyItem;
			state.fontStyle = effectiveFont.fontStyle;
			return (
				<FlexLayout
					className={`${baseClassName}__row`}
					direction='column'
					data-tid={`${baseTid}_fontstyle`}
				>
					<Label
						label={label}
						type='caption'
					/>
					<Select
						options={fontFamilyItem.subCssClasses}
						value={state.fontStyle}
						displayLabel={label}
						onChange={fontStyle => this.modifyState({ fontStyle })}
					/>
				</FlexLayout>
			);
		}

		_renderSizeAndColour(state) {
			return (
				<FlexLayout
					className={`${baseClassName}__row`}
					direction='row'
				>
					<FlexItem
						grow='1'
						shrink='1'
						className={`${baseClassName}__firstCellInRow`}
					>
						{this._renderFontSize(state)}
					</FlexItem>
					<FlexItem
						grow='1'
						shrink='1'
					>
						{this._renderColorPicker(state)}
					</FlexItem>
				</FlexLayout>
			);
		}

		_renderFontSize(state) {
			const label = StringResources.get('fontSizeSelect');
			const fontSize = state.fontSize ? state.fontSize : this.props.fontSizeOptions[0].value;
			return (
				<FlexLayout
					direction='column'
					data-tid={`${baseTid}_fontSize`}
				>
					<Label
						label={label}
						type='caption'
					/>
					<Select
						options={this.props.fontSizeOptions}
						value= {fontSize}
						displayLabel={label}
						onChange={fontSize => this.modifyState({ fontSize })}
					/>
				</FlexLayout>
			);
		}

		_renderColorPicker(state) {
			const label = StringResources.get('fontColorSelect');
			return (
				<FlexLayout
					direction='column'
					data-tid={`${baseTid}_color`}
				>
					<Label
						label={label}
						type='caption'
					/>
					<ColorPicker
						colors={this.props.colorOptions}
						selectedColor={state.color}
						title={label}
						onColorSelected={color => this.modifyState({ color })}
					/>
				</FlexLayout>
			);
		}

		_renderStyleAndAligment(state) {
			return (
				<FlexLayout
					className={`${baseClassName}__row`}
					direction='row'
				>
					<FlexItem
						grow='1'
						shrink='1'
						className={`${baseClassName}__firstCellInRow`}
					>
						{this._renderStyleButtons(state)}
					</FlexItem>
					<FlexItem
						grow='1'
						shrink='1'
					>
						{this._renderAlignmentButtons(state)}
					</FlexItem>
				</FlexLayout>
			);
		}

		_renderStyleButtons(state) {
			const boldItem = {
				name: 'bold',
				icon: boldIcon.id,
				description: StringResources.get('bold'),
				checked: state.bold,
				onChange: () => this.onToggleFormatClick('bold')
			};

			const italicItem = {
				name: 'italic',
				icon: italicIcon.id,
				description: StringResources.get('italic'),
				checked: state.italic,
				onChange: () => this.onToggleFormatClick('italic')
			};

			const underlineItem = {
				name: 'underline',
				icon: underlineIcon.id,
				description: StringResources.get('underline'),
				checked: state.underline,
				onChange: () => this.onToggleFormatClick('underline')
			};

			const items = [boldItem, italicItem, underlineItem];

			const label = StringResources.get('fontStyle');
			return (
				<FlexLayout
					direction='column'
					data-tid={`${baseTid}_style`}
				>
					<Label
						label={label}
						type='caption'
					/>
					<IconButtonToggleGroup
						items={items}
					/>
				</FlexLayout>
			);
		}

		_renderAlignmentButtons(state) {
			const leftItem = {
				name: 'left',
				icon: leftAlignIcon.id,
				description: StringResources.get('justifyLeft'),
				checked: state.textAlign === ALIGN_ENUM.LEFT,
				onChange: value => this.onAlignmentClick(value, ALIGN_ENUM.LEFT)
			};

			const centerItem = {
				name: 'center',
				icon: centerAlignIcon.id,
				description: StringResources.get('justifyCenter'),
				checked: state.textAlign === ALIGN_ENUM.CENTER,
				onChange: value => this.onAlignmentClick(value, ALIGN_ENUM.CENTER)
			};

			const rightItem = {
				name: 'right',
				icon: rightAlignIcon.id,
				description: StringResources.get('justifyRight'),
				checked: state.textAlign === ALIGN_ENUM.RIGHT,
				onChange: value => this.onAlignmentClick(value, ALIGN_ENUM.RIGHT)
			};

			const wrapItem = {
				name: 'wrap',
				icon: textWrapIcon.id,
				description: StringResources.get('textWrap'),
				checked: state.textWrap,
				onChange: () => this.onToggleFormatClick('textWrap')
			};

			const items = [leftItem, centerItem, rightItem];

			if (this.props.supportsTextWrap) {
				items.push(wrapItem);
			}

			const label = StringResources.get('fontAlignment');

			return (
				<FlexLayout
					direction='column'
					data-tid={`${baseTid}_alignment`}
				>
					<Label
						label={label}
						type='caption'
					/>
					<IconButtonToggleGroup
						items={items}
					/>
				</FlexLayout>
			);
		}

		_renderRestoreButton() {
			const label = StringResources.get('restore');
			return (
				<FlexItem
					alignSelf='flex-start'
				>
					<Button
						label={label}
						icon={resetIcon.id}
						data-tid={`${baseTid}_restore`}
						intent="primary"
						variant="frameless"
						disabled={this.isDefault()}
						onClick={() => this.onRestoreButtonClicked()}
					/>
				</FlexItem>
			);
		}

		_renderFooterButtons() {
			const cancelLabel = StringResources.get('dlg_cancel');
			const saveLabel = StringResources.get('dlg_save');
			return (
				<React.Fragment>
					<FlexItem grow='1' shrink='1'>
						<Button
							className={`${baseClassName}__cancel`}
							label={cancelLabel}
							data-tid={`${baseTid}_cancel`}
							size='small'
							variant='solid'
							onClick={() => this.onCancelClicked()}
						/>
					</FlexItem>
					<FlexItem grow='1' shrink='1'>
						<Button
							className={`${baseClassName}__ok`}
							size='small'
							label={saveLabel}
							data-tid={`${baseTid}_done`}
							variant='solid'
							onClick={() => this.onOkClicked()}
						/>
					</FlexItem>
				</React.Fragment>
			);
		}
	}

	return TextFormatView;
});
